import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { conns } from '@cfg/axiosConfig.tsx';

import type {
  CustomerBalanceAdjustmentResponse,
  PosCustomerBalanceEntry,
  PosCustomerBalanceSummary,
} from '../types';

export interface ApplyCreditPayload {
  amount: number;
  idempotency_key?: string;
  note?: string;
}

export interface ApplyCreditResponse {
  entry: PosCustomerBalanceEntry;
  balance: PosCustomerBalanceSummary;
}

export interface BalanceAdjustmentPayload {
  amount: number;
  direction: 'credit' | 'debit';
  payment_method_id: number;
  note?: string | null;
  idempotency_key?: string;
}

const fetchCustomerBalance = async (
  customerId: number,
): Promise<PosCustomerBalanceSummary> => {
  const { data } = await conns<PosCustomerBalanceSummary>(
    'erp:customers:balance',
    [customerId, 'balance'],
  ).get();
  return data;
};

export const useCustomerBalance = (
  customerId: number | null,
  options?: Omit<UseQueryOptions<PosCustomerBalanceSummary>, 'queryKey' | 'queryFn'>,
): UseQueryResult<PosCustomerBalanceSummary, Error> => {
  return useQuery({
    queryKey: ['pos', 'customer-balance', customerId],
    queryFn: () => fetchCustomerBalance(customerId as number),
    enabled: customerId != null,
    staleTime: 30_000,
    ...options,
  });
};

const applyCustomerCredit = async (
  orderId: number,
  payload: ApplyCreditPayload,
): Promise<ApplyCreditResponse> => {
  const { data } = await conns<ApplyCreditResponse>(
    'erp:orders:payments:apply_credit',
    [orderId, 'payments', 'apply-credit'],
  ).post(payload);
  return data;
};

export const adjustCustomerBalance = async (
  customerId: number,
  payload: BalanceAdjustmentPayload,
): Promise<CustomerBalanceAdjustmentResponse> => {
  const { data } = await conns<CustomerBalanceAdjustmentResponse>(
    'erp:customers:balance_adjust',
    [customerId, 'balance', 'adjust'],
  ).post(payload);
  return data;
};

export const useApplyCustomerCredit = (): UseMutationResult<
  ApplyCreditResponse,
  Error,
  { orderId: number; payload: ApplyCreditPayload }
> => {
  return useMutation({
    mutationFn: ({ orderId, payload }) => applyCustomerCredit(orderId, payload),
  });
};

export { fetchCustomerBalance, applyCustomerCredit };
